import { FlexibleConnectedPositionStrategy, Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { ComponentRef, Directive, ElementRef, HostListener, Input, OnInit } from "@angular/core";
import { TooltipComponent } from "./tooltip.component";

@Directive({
  selector: '[appTooltip]',
  standalone: true
})
export class TooltipDirective implements OnInit {
  @Input('appTooltip') content: string;
  @Input('appTooltipDisabled') disabled: boolean = false;

  private overlayRef: OverlayRef;
  private isOverlayDisplayed: boolean;
  private timeout: ReturnType<typeof setTimeout>;
  private isTouchMoving: boolean = false;
  private positionStrategy: FlexibleConnectedPositionStrategy;

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay
  ) { }

  ngOnInit() {
    this.positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
        { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }
      ])
      .withDefaultOffsetY(-10);

    this.overlayRef = this.overlay.create({ positionStrategy: this.positionStrategy });
  }

  @HostListener('document:touchstart', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.hide();
    }
  }

  @HostListener('touchstart', ['$event'])
  onToucheStart() {
    this.isTouchMoving = false;
  }

  @HostListener('touchmove', ['$event'])
  onToucheMove() {
    this.isTouchMoving = true;
  }

  @HostListener('mouseenter', ['$event'])
  @HostListener('touchend', ['$event'])
  show(event: TouchEvent | MouseEvent) {
    if (this.disabled || this.isOverlayDisplayed || this.isTouchMoving) {
      if (event instanceof TouchEvent && this.isOverlayDisplayed) {
        this.hide();
      }
      return;
    }

    this.isOverlayDisplayed = true;
    const tooltipPortal = new ComponentPortal(TooltipComponent);

    if (this.overlayRef.hasAttached()) {
      this.hide();
    }

    const tooltipRef: ComponentRef<TooltipComponent> = this.overlayRef.attach(tooltipPortal);
    tooltipRef.instance.text = this.content;

    setTimeout(() => {
      const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).changedTouches[0].clientX;
      const rect = tooltipRef.location.nativeElement.getBoundingClientRect();
      const customTooltipEl = tooltipRef.location.nativeElement.firstElementChild;
      const leftValue = Math.round((clientX - rect.x) / rect.width * 100);
      customTooltipEl.style.setProperty('--custom-left',
        leftValue < 0
          ? '4px'
          : leftValue > 100
            ? 'calc(100% - 14px)'
            : leftValue + '%'
      );
      customTooltipEl.style.setProperty('--custom-top', this.isTooltipVisible() ? '100%' : '-12px');
      customTooltipEl.style.setProperty('--custom-border-color',
        this.isTooltipVisible()
          ? 'var(--item-bg-color) transparent transparent transparent'
          : 'transparent transparent var(--item-bg-color) transparent');
      tooltipRef.instance.show();
    }, 0);

    this.isTooltipVisible()
      ? this.positionStrategy.withDefaultOffsetY(-10)
      : this.positionStrategy.withDefaultOffsetY(64);

    this.timeout = setTimeout(() => {
      this.hide();
    }, 3000);
  }

  @HostListener('mouseout')
  hide() {
    this.overlayRef.detach();
    this.isOverlayDisplayed = false;
    clearTimeout(this.timeout);
  }

  private isTooltipVisible() {
    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipHeight = 46;

    return elementRect.top > tooltipHeight;
  }
}